export const CONTRACT_DERIVED_CONSTRUCTION_KEYS = [
  "construction_contractor",
  "contract_cost",
  "contract_execution_date",
  "contract_expiration_date",
  "fiber_contractor",
];

export function overlayContractDerivedConstruction(domains, derivedFields) {
  if (!derivedFields || typeof derivedFields !== "object") return domains;
  const cons = { ...(domains?.construction_details || domains?.construction || {}) };
  for (const key of CONTRACT_DERIVED_CONSTRUCTION_KEYS) {
    if (Object.prototype.hasOwnProperty.call(derivedFields, key)) {
      cons[key] = derivedFields[key];
    }
  }
  return {
    ...domains,
    construction_details: cons,
    construction: cons,
  };
}

/**
 * Prefer contract-derived value when the project is linked (even if blank),
 * so fiber-only / construction-only roles clear the other field instead of
 * falling back to stale ACF text.
 */
export function resolveContractOrAcfField(contractSummary, fieldKey, acfValue) {
  const fields = contractSummary?.fields;
  if (fields && typeof fields === "object" && Object.prototype.hasOwnProperty.call(fields, fieldKey)) {
    return fields[fieldKey];
  }
  return acfValue;
}

function contractRoleApplies(contract, role) {
  if (role === "fiber") return contract?.appliesFiber !== false;
  return contract?.appliesConstruction !== false;
}

function contractorNameFromContract(contract, role) {
  if (!contract) return "";
  if (role === "fiber") return String(contract.fiberContractor || "").trim();
  const status = contract.status;
  if (status === "cancelled") return "Cancelled";
  if (status === "pending_award") {
    return String(contract.contractor || "").trim() || "Pending Award";
  }
  return String(contract.contractor || "").trim();
}

/**
 * Format contractor labels for a role from the contracts list.
 * @param {object|null} contractSummary
 * @param {"construction"|"fiber"} role
 * @param {{ includeCoverage?: boolean, formatFootage?: (n:number)=>string }} [options]
 * @returns {string[]}
 */
export function listContractorsForRole(contractSummary, role, options = {}) {
  const includeCoverage = Boolean(options.includeCoverage);
  const formatFootage =
    typeof options.formatFootage === "function"
      ? options.formatFootage
      : (n) => Number(n).toLocaleString("en-US");

  const contracts = Array.isArray(contractSummary?.contracts) ? contractSummary.contracts : [];
  if (contracts.length) {
    const seen = new Set();
    const parts = [];
    for (const contract of contracts) {
      if (!contractRoleApplies(contract, role)) continue;
      const name = contractorNameFromContract(contract, role);
      if (!name) continue;
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      const covered = Number(contract.coveredFootage);
      if (includeCoverage && Number.isFinite(covered) && covered > 0) {
        parts.push(`${name} (${formatFootage(covered)} ft)`);
      } else {
        parts.push(name);
      }
    }
    if (parts.length) return parts;
  }

  const fieldKey = role === "fiber" ? "fiber_contractor" : "construction_contractor";
  const fromFields = contractSummary?.fields?.[fieldKey];
  if (fromFields != null && String(fromFields).trim() !== "") {
    return String(fromFields)
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
  }
  return [];
}

export function formatContractorsForRole(contractSummary, role, options = {}) {
  return listContractorsForRole(contractSummary, role, options).join(", ");
}

export function stripContractDerivedFromConstruction(cons) {
  if (!cons || typeof cons !== "object") return cons;
  const next = { ...cons };
  for (const key of CONTRACT_DERIVED_CONSTRUCTION_KEYS) {
    delete next[key];
  }
  return next;
}
